import { useApolloClient } from "@apollo/client";
import { get, isNil } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { COMPUTE_FIELD } from "utils/api/graphql/queries/field-registry";
import { buildBatchFactorPriceArgs } from "./computeFieldArgs";
import { INITIAL_COMPUTE_STATE, beginCompute, endCompute } from "./computeBusy";
import {
  nextDisplayedPrices,
  readCachedPrices,
  storePrices,
} from "./factorPrices";
import { currentPriceCacheScope, syncPriceCacheScope } from "./priceCacheScope";

export const useFactorPrices = (
  project,
  slots,
  prefetchPlanIds = [],
  scopeKey = "",
) => {
  const client = useApolloClient();
  const [factorPrices, setFactorPrices] = useState({});
  const [computeState, setComputeState] = useState(INITIAL_COMPUTE_STATE);
  const scopeRef = useRef(null);
  const planRef = useRef(null);

  const pricedSlots = useMemo(
    () =>
      (slots || []).filter(
        (slot) =>
          get(slot, "binding.kind") === "data" &&
          !isNil(get(slot, "binding.factors")) &&
          !isNil(get(slot, "binding.source")),
      ),
    [slots],
  );

  const projectId = get(project, "id");
  const insurancePlanId = get(project, "fields.selectedPlan");
  const partnerCode = get(project, "fields.partnerCode") || "";
  const signature = JSON.stringify(
    pricedSlots.map((slot) => [slot.binding.source, slot.binding.factors]),
  );
  const prefetchKey = (prefetchPlanIds || []).filter(Boolean).join(",");

  useEffect(() => {
    // A tariff is only reusable inside the visit that computed it: leaving the
    // screen or touching a priced input drops every cached price, and the
    // displayed one, so a stale amount is never shown as final.
    syncPriceCacheScope(scopeKey);
    if (scopeRef.current !== null && scopeRef.current !== scopeKey) {
      setFactorPrices({});
    }
    scopeRef.current = scopeKey;

    if (!projectId || !insurancePlanId || pricedSlots.length === 0) {
      setComputeState(endCompute);
      return;
    }

    let cancelled = false;
    const isStale = () => cancelled || currentPriceCacheScope() !== scopeKey;
    const loadPlan = async (planId) => {
      const cached = readCachedPrices({
        projectId,
        insurancePlanId: planId,
        partnerCode,
        pricedSlots,
      });
      if (cached) return cached;
      const { data } = await client.query({
        query: COMPUTE_FIELD,
        variables: {
          project: { id: projectId },
          key: "insurancePlanPrice",
          args: buildBatchFactorPriceArgs({
            insurancePlanId: planId,
            partnerCode,
            variants: pricedSlots.map((slot) => ({
              source: slot.binding.source,
              factors: slot.binding.factors,
            })),
          }),
        },
        fetchPolicy: "network-only",
      });
      if (isStale()) return null;
      return storePrices({
        projectId,
        insurancePlanId: planId,
        partnerCode,
        pricedSlots,
        payload: get(data, "computeField") || {},
      });
    };

    const run = async () => {
      const cached = readCachedPrices({
        projectId,
        insurancePlanId,
        partnerCode,
        pricedSlots,
      });
      const displayed = nextDisplayedPrices({
        cached,
        planChanged: planRef.current !== insurancePlanId,
      });
      planRef.current = insurancePlanId;
      if (displayed !== undefined) setFactorPrices(displayed);
      if (cached) {
        setComputeState(endCompute);
      } else {
        setComputeState(beginCompute);
        try {
          const next = await loadPlan(insurancePlanId);
          if (!isStale() && next) {
            setFactorPrices(next);
            setComputeState(endCompute);
          }
        } catch (_) {
          if (!cancelled) setComputeState(endCompute);
        }
      }

      const others = (prefetchPlanIds || []).filter(
        (id) => id && id !== insurancePlanId,
      );
      for (const planId of others) {
        if (isStale()) return;
        try {
          await loadPlan(planId);
        } catch (_) {}
      }
    };
    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectId,
    insurancePlanId,
    partnerCode,
    signature,
    prefetchKey,
    scopeKey,
    client,
  ]);

  return { factorPrices, computeState };
};
